import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import config from "../../config";
import Swal from "sweetalert2";
import { saveAs } from "file-saver";

function Login() {
  const [rollCode, setRollCode] = useState("");
  const [rollNumber, setRollNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);

  // ── MATH CAPTCHA STATE ─────────────────────────────────────────
  const [captchaAnswer, setCaptchaAnswer] = useState(null);
  const [userCaptcha, setUserCaptcha] = useState("");
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const canvasRef = useRef(null);
  // ───────────────────────────────────────────────────────────────

  const [notices, setNotices] = useState([]);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [isOtpVerifying, setIsOtpVerifying] = useState(false);

  const [otp1, setOtp1] = useState("");
  const [otp2, setOtp2] = useState("");
  const [otp3, setOtp3] = useState("");
  const [otp4, setOtp4] = useState("");
  const [otp5, setOtp5] = useState("");
  const [otp6, setOtp6] = useState("");

  const otp1Ref = useRef(null);
  const otp2Ref = useRef(null);
  const otp3Ref = useRef(null);
  const otp4Ref = useRef(null);
  const otp5Ref = useRef(null);
  const otp6Ref = useRef(null);

  const [resendDisabled, setResendDisabled] = useState(false);
  const [timer, setTimer] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    generateCaptcha();
    fetchNotices();
    sessionStorage.clear();
  }, []);

  useEffect(() => {
    let interval;
    if (resendDisabled && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else if (timer === 0) {
      setResendDisabled(false);
      sessionStorage.setItem("LoginOtp", "");
    }
    return () => clearInterval(interval);
  }, [resendDisabled, timer]);

  const fetchNotices = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/UnAuthorizedSelectNoticeDataClass10`);
      const data = await response.json();
      if (Array.isArray(data.data?.["select-notice"])) {
        setNotices(data.data?.["select-notice"]);
      }
    } catch (error) {
      console.error("Error fetching notices:", error);
    }
  };

  // ── MATH CAPTCHA: generate + draw on canvas ────────────────────
  const generateCaptcha = () => {
    const ops = [
      { symbol: "*", fn: (a, b) => a * b },
      { symbol: "+", fn: (a, b) => a + b },
      { symbol: "-", fn: (a, b) => a - b },
    ];
    const op = ops[Math.floor(Math.random() * ops.length)];
    const a = Math.floor(Math.random() * 15) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    const answer = op.fn(a, b);
    const question = `${a} ${op.symbol} ${b} = ?`;
    setCaptchaAnswer(answer);
    setUserCaptcha("");
    setIsCaptchaValid(false);
    setTimeout(() => drawCaptcha(question), 0);
  };

  const drawCaptcha = (text) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;

    // Background
    ctx.clearRect(0, 0, W, H);
    const bgGrad = ctx.createLinearGradient(0, 0, W, H);
    bgGrad.addColorStop(0, "#eef2ff");
    bgGrad.addColorStop(1, "#fef9c3");
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, W, H);

    // Noise dots
    for (let i = 0; i < 200; i++) {
      ctx.beginPath();
      ctx.arc(Math.random() * W, Math.random() * H, Math.random() * 1.5, 0, 2 * Math.PI);
      ctx.fillStyle = `rgba(${Math.floor(Math.random()*180)},${Math.floor(Math.random()*180)},${Math.floor(Math.random()*180)},${(Math.random()*0.5+0.2).toFixed(2)})`;
      ctx.fill();
    }

    // Wavy interference lines
    for (let l = 0; l < 6; l++) {
      ctx.beginPath();
      ctx.moveTo(0, Math.random() * H);
      for (let x = 0; x < W; x += 10) ctx.lineTo(x, Math.random() * H);
      ctx.strokeStyle = `rgba(${Math.floor(Math.random()*200)},${Math.floor(Math.random()*200)},${Math.floor(Math.random()*200)},0.25)`;
      ctx.lineWidth = Math.random() * 1.5 + 0.5;
      ctx.stroke();
    }

    // Sine wave overlays
    for (let s = 0; s < 3; s++) {
      ctx.beginPath();
      const amp = 4 + Math.random() * 4;
      const freq = 0.04 + Math.random() * 0.03;
      const yBase = H * (0.3 + s * 0.2);
      ctx.moveTo(0, yBase);
      for (let x = 0; x < W; x++) ctx.lineTo(x, yBase + Math.sin(x * freq) * amp);
      ctx.strokeStyle = `rgba(${Math.floor(Math.random()*150)},${Math.floor(Math.random()*100)},${Math.floor(Math.random()*200)},0.2)`;
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // Draw each character with random rotation, color, font, size
    const fonts = ["'Courier New'", "Georgia", "Arial Black", "Verdana"];
    const chars = text.split("");
    const startX = 14;
    const spacing = (W - startX * 2) / chars.length;

    chars.forEach((char, i) => {
      const x = startX + i * spacing + spacing / 2;
      const y = H / 2 + (Math.random() - 0.5) * 10;
      const angle = (Math.random() - 0.5) * 0.45;
      const size = 20 + Math.floor(Math.random() * 8);
      const font = fonts[Math.floor(Math.random() * fonts.length)];
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.shadowColor = "rgba(0,0,0,0.25)";
      ctx.shadowBlur = 2;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 1;
      ctx.font = `bold ${size}px ${font}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = `rgb(${Math.floor(Math.random()*80)},${Math.floor(Math.random()*80)},${Math.floor(Math.random()*160+60)})`;
      ctx.fillText(char, 0, 0);
      ctx.restore();
    });

    // Border
    ctx.strokeStyle = "#cbd5e1";
    ctx.lineWidth = 1.5;
    ctx.strokeRect(1, 1, W - 2, H - 2);
  };
  // ───────────────────────────────────────────────────────────────

  const validateCaptcha = (e) => {
    const val = e.target.value;
    setUserCaptcha(val);
    setIsCaptchaValid(parseInt(val, 10) === captchaAnswer);
  };

  const handleLoginWithCaptcha = async () => {
    if (!rollCode) { Swal.fire("Warning", "Please enter Roll Code.", "warning"); return; }
    if (!rollNumber) { Swal.fire("Warning", "Please enter Roll Number.", "warning"); return; }
    if (!phoneNumber) { Swal.fire("Warning", "Please enter Phone Number.", "warning"); return; }
    if (phoneNumber.length !== 10) { Swal.fire("Warning", "Phone Number must be exactly 10 digits.", "warning"); return; }
    if (!isCaptchaValid) {
      Swal.fire({ icon: "error", title: "Invalid CAPTCHA", text: "Please solve the math problem correctly.", confirmButtonColor: "#d33" });
      generateCaptcha();
      return;
    }
    await handleLogin();
    generateCaptcha();
  };

  const handleLogin = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/StudentLoginClass10`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rollcode: rollCode, rollno: rollNumber }),
      });
      const data = await response.json();
      console.log("Login response:", data);
      if (response.ok) {
        sessionStorage.setItem("rollcode", rollCode);
        sessionStorage.setItem("rollnumber", rollNumber);
        sessionStorage.setItem("candidatename", data.data.candidateName);
        sessionStorage.setItem("developmentMode", data.data.developmentMode);
        sessionStorage.setItem("candidateContactNo", data.data.contactNo);
        sessionStorage.setItem("candidateEmail", data.data.email);
        sessionStorage.setItem("count", "1");
        debugger;
        if (data.data.class10Otp === "YES") {
          handleSentOtp();
          sessionStorage.setItem("tokenclassxwithoutotp", data.token);
        } else {
          sessionStorage.setItem("tokenclassx", data.token);
          navigate("/dashboard");
        }
      } else {
        Swal.fire("Error", data?.message || "Invalid login credentials", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Something went wrong. Try again later.", "error");
      console.error("Login error:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateOTP = () => Math.floor(100000 + Math.random() * 900000).toString();

  const sendMobileOTP = async (contactNumber, otp) => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/SendOtp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contactNumber, otp }),
      });
      if (response.ok) { console.log("OTP sent:", await response.json()); return true; }
      return false;
    } catch (error) { console.error("Error sending OTP:", error); return false; }
  };

  const handleSentOtp = async () => {
    const developmentMode = sessionStorage.getItem("developmentMode");
    const rollcode = sessionStorage.getItem("rollcode");
    const rollnumber = sessionStorage.getItem("rollnumber");
    const contact_no = phoneNumber;
    let otp = "", isSent = false;
    if (developmentMode === "YES") { otp = "123456"; isSent = true; }
    else { otp = generateOTP(); isSent = await sendMobileOTP(contact_no, otp); }
    if (isSent) {
      sessionStorage.setItem("LoginOtp", `${rollcode}|${rollnumber}|${otp}`);
      sessionStorage.setItem("phoneNumber", phoneNumber);
      setOtp1(""); setOtp2(""); setOtp3(""); setOtp4(""); setOtp5(""); setOtp6("");
      setShowOtpModal(true);
      setResendDisabled(true);
      otp1Ref.current?.focus();
      setTimer(180);
    } else {
      Swal.fire("Error", "Failed to send OTP. Please try again.", "error");
    }
  };

  const handleOtpInput = (e, setPart, maxLength, nextRef) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, maxLength);
    setPart(value);
    if (value.length === maxLength && nextRef?.current) nextRef.current.focus();
  };

  const handleOtpBackspace = (e, value, prevRef) => {
    if (e.key === "Backspace" && value === "" && prevRef?.current) prevRef.current.focus();
  };

  const handleOtpSubmit = () => {
    setIsOtpVerifying(true);
    const otp = [otp1, otp2, otp3, otp4, otp5, otp6].join("");
    const LoginOtp = sessionStorage.getItem("LoginOtp");
    const rollcode = sessionStorage.getItem("rollcode");
    const rollnumber = sessionStorage.getItem("rollnumber");
    const tokenWithoutOtp = sessionStorage.getItem("tokenclassxwithoutotp");
    if (LoginOtp) {
      const [extractedRollCode, extractedRollNumber, extractedOtp] = LoginOtp.split("|");
      if (otp.length !== 6) { Swal.fire("Warning", "Please enter all 6 digits of the OTP.", "warning"); setIsOtpVerifying(false); return; }
      if (rollcode === extractedRollCode && rollnumber === extractedRollNumber && otp === extractedOtp) {
        sessionStorage.setItem("tokenclassx", tokenWithoutOtp);
        sessionStorage.removeItem("tokenclassxwithoutotp");
        sessionStorage.removeItem("LoginOtp");
        loginactivity();
      } else {
        Swal.fire("Error", "Invalid OTP. Please try again.", "error");
        setIsOtpVerifying(false);
      }
    } else {
      Swal.fire("Error", "Invalid OTP. Please try again.", "error");
      setIsOtpVerifying(false);
    }
  };

  const loginactivity = async () => {
    const token = sessionStorage.getItem("tokenclassx");
    try {
      const response = await fetch(`${config.API_BASE_URL}/InsertLogDataStudentLogin`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          ROLL_CODE: sessionStorage.getItem("rollcode"),
          ROLL_NO: sessionStorage.getItem("rollnumber"),
          CONTACT_NO: sessionStorage.getItem("phoneNumber"),
          ACTIVITY: "LOGIN",
        }),
      });
      if (response.ok) navigate("/dashboard");
    } catch (error) {
      console.error("Error in login activity:", error);
    }
  };

  return (
    <div className="container-scroller">
      <header className="fixed-top bg-brown">
        <div className="container">
          <div className="d-flex align-items-center" style={{ margin: "5px 0px" }}>
            <img src="../assets/images/jac-academic-council.png" alt="JAC Logo" style={{ height: "70px", marginRight: "15px" }} className="banner-img" />
            <div>
              <h3 className="logo-firsttext text-white" style={{ marginTop: 5 }}>Jharkhand Academic Council, Ranchi</h3>
              <h4 className="logo-secondtext text-white">Scrutiny Portal 2026 ( Class - X )</h4>
            </div>
          </div>
        </div>
      </header>

      <div className="container-fluid page-body-wrapper full-page-wrapper">
        <div className="content-wrapper d-flex align-items-center auth px-0">
          <div className="container">
            <div className="row w-100">
              <div className="col-md-8 stretch-card">
                <div className="card">
                  <div className="card-header bg-brown">
                    <h4 className="mb-0 p-2">NOTICE</h4>
                  </div>
                  <div className="card-body" style={{ height: "477px", overflowY: "scroll" }}>
                    <table className="table table-striped table-bordered table-condensed">
                      <thead>
                        <tr>
                          <th style={{ width: "15%" }}>Date</th>
                          <th style={{ width: "75%" }}>Details</th>
                          <th style={{ width: "10%" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {notices.length > 0 ? (
                          notices.map((notice, index) => (
                            <tr key={notice.ID || index}>
                              <td>{(notice.SHOWING_DATE || notice.showinG_DATE)?.split("-").reverse().join("-")}</td>
                              <td>
                                <div className="blink" style={{ fontSize: "14px" }}>
                                  <strong className="text-danger">NOTICE {index + 1}:</strong>{" "}
                                  {notice.NOTICE_DESCRIPTION || notice.noticE_DESCRIPTION || "No description available."}
                                </div>
                              </td>
                              <td>
                                {notice.NOTICE_FILE_URL ? (
                                  <button className="btn btn-sm btn-danger" onClick={async () => {
                                    try {
                                      const fileUrl = notice.NOTICE_FILE_URL.replace("http://", "https://");
                                      const response = await fetch(fileUrl, { mode: "cors" });
                                      const blob = await response.blob();
                                      saveAs(blob, "Notice.pdf");
                                      const blobUrl = URL.createObjectURL(blob);
                                      window.open(blobUrl, "_blank", "width=900,height=700");
                                      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
                                    } catch (error) { console.error("Download failed:", error); alert("Failed to download the file."); }
                                  }}>
                                    <i className="fa fa-file-pdf-o"></i> Download
                                  </button>
                                ) : "-"}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan="3" className="text-center text-muted">No notices available.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="auth-form-light text-left py-3 px-3">
                  <h4 className="text-center mb-3">Student Login</h4>
                  <form className="pt-2" onSubmit={(e) => { e.preventDefault(); handleLoginWithCaptcha(); }}>
                    <div className="form-group">
                      <h6 className="font-weight-light">Roll Code</h6>
                      <input type="text" maxLength={10} className="form-control form-control-xs" placeholder="Enter Your Roll Code" value={rollCode} onChange={(e) => setRollCode(e.target.value.replace(/[^0-9]/g, ""))} />
                    </div>
                    <div className="form-group">
                      <h6 className="font-weight-light">Roll Number</h6>
                      <input type="text" className="form-control form-control-xs" placeholder="Enter Your Roll Number" value={rollNumber} onChange={(e) => setRollNumber(e.target.value.replace(/[^0-9]/g, ""))} />
                    </div>
                    <div className="form-group">
                      <h6 className="font-weight-light">Phone Number</h6>
                      <input type="text" className="form-control form-control-xs" placeholder="Enter Your Phone Number" maxLength={10} minLength={10} value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ""))} />
                    </div>

                    {/* ── MATH CAPTCHA ON CANVAS ─────────────────────────── */}
                    <div className="form-group mb-2">
                      <h6 className="font-weight-light">Solve to Verify</h6>
                      <div className="d-flex align-items-center mb-2">
                        <canvas
                          ref={canvasRef}
                          width="200"
                          height="50"
                          style={{ border: "1px solid #ddd", borderRadius: "6px", cursor: "not-allowed", userSelect: "none" }}
                        />
                        <button type="button" onClick={generateCaptcha} className="btn btn-secondary" style={{ marginLeft: "8px" }} title="Refresh CAPTCHA">
                          ↻ Refresh
                        </button>
                      </div>
                      <input type="number" className="form-control" placeholder="Enter the answer" value={userCaptcha} onChange={validateCaptcha} />
                    </div>
                    {/* ────────────────────────────────────────────────────── */}

                    <div className="mt-1">
                      <button type="submit" className="btn btn-block bg-brown btn-xs text-white" style={{ padding: "10px" }} disabled={loading}>
                        {loading ? "Logging in..." : "Login"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="fixed-bottom bg-brown text-center text-white" style={{ padding: "8px 0" }}>
        <p style={{ marginBottom: 0, fontSize: "12px" }}>Copyright &copy; JAC. All rights reserved.</p>
      </footer>

      {showOtpModal && (
        <div className="modal fade show" tabIndex="-1" aria-modal="true" role="dialog"
          style={{ display: "flex", backgroundColor: "rgba(0,0,0,0.5)", height: "100vh", alignItems: "center" }}>
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <form onSubmit={(e) => { e.preventDefault(); handleOtpSubmit(); }}>
                <div className="modal-header">
                  <h5 className="modal-title">OTP Verification</h5>
                  <button type="button" className="btn close" aria-label="Close" onClick={() => { setShowOtpModal(false); sessionStorage.clear(); }}>
                    <i className="fa fa-times"></i>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="row" style={{ padding: "0px 25px" }}>
                    {[
                      { ref: otp1Ref, value: otp1, set: setOtp1, next: otp2Ref, prev: null },
                      { ref: otp2Ref, value: otp2, set: setOtp2, next: otp3Ref, prev: otp1Ref },
                      { ref: otp3Ref, value: otp3, set: setOtp3, next: otp4Ref, prev: otp2Ref },
                      { ref: otp4Ref, value: otp4, set: setOtp4, next: otp5Ref, prev: otp3Ref },
                      { ref: otp5Ref, value: otp5, set: setOtp5, next: otp6Ref, prev: otp4Ref },
                      { ref: otp6Ref, value: otp6, set: setOtp6, next: null,    prev: otp5Ref },
                    ].map((field, index) => (
                      <div className="col-2" key={index} style={{ padding: "0px 10px" }}>
                        <input type="tel" ref={field.ref} value={field.value}
                          onChange={(e) => handleOtpInput(e, field.set, 1, field.next)}
                          onKeyDown={(e) => handleOtpBackspace(e, field.value, field.prev)}
                          className="form-control form-control-sm text-center number"
                          maxLength="1" pattern="[0-9]*" inputMode="numeric" autoComplete="one-time-code" autoFocus={index === 0} />
                      </div>
                    ))}
                  </div>
                  <p id="timer" className="text-center text-danger" style={{ margin: "5px 0px" }}>
                    {resendDisabled ? `Resend in ${timer} seconds` : "You can resend OTP now"}
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-warning" id="resendBtn" onClick={handleSentOtp} disabled={resendDisabled}>Resend OTP</button>
                  <button type="submit" className={`btn text-white ${isOtpVerifying ? "bg-danger" : "bg-brown"}`} id="vrfyotp" disabled={isOtpVerifying}>
                    <i className={`fa ${isOtpVerifying ? "fa fa-refresh fa-spin" : ""}`} aria-hidden="true"></i>
                    &nbsp;{isOtpVerifying ? "Verifying ..." : "Verify OTP"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;